import { useId, useState } from 'react';

import { formatDay, formatGain } from './format';

/** Figma chart frame: 96px tall, grid lines at 24 / 48 / 72. */
const HEIGHT = 96;
const GRID = [24, 48, 72];
/** SVG user units across; the SVG stretches to the card's width. */
const WIDTH = 100;

type AreaChartProps = {
  /** One value per day. */
  values: number[];
  /** Top of the y-axis. */
  max: number;
  /** Bottom of the y-axis (0 unless a day lost ground). */
  min?: number;
  /** The day of `values[0]`. */
  start: Date;
  /** Series colour (a `viz-*` token). */
  color: string;
  /** Accessible name, e.g. "Views, last 30 days". */
  label: string;
};

/**
 * Figma metric chart (166:287): a 2px line over a fading area, ending in a
 * dot, on three recessive grid lines. Hovering shows a crosshair and the
 * day's value. The line uses the same bump curve Figma exported (horizontal
 * tangents at each point).
 */
export function AreaChart({ values, max, min = 0, start, color, label }: AreaChartProps) {
  const gradientId = useId();
  const [active, setActive] = useState<number | null>(null);
  const step = WIDTH / (values.length - 1);
  const span = max - min || 1;
  const points = values.map(
    (v, i) => [i * step, HEIGHT - ((v - min) / span) * HEIGHT] as const,
  );

  const [first, ...rest] = points;
  let line = `M${first![0]} ${first![1]}`;
  rest.forEach(([x, y], i) => {
    const [px, py] = points[i]!;
    const mid = (px + x) / 2;
    line += ` C${mid} ${py} ${mid} ${y} ${x} ${y}`;
  });
  const area = `${line} L${WIDTH} ${HEIGHT} L0 ${HEIGHT} Z`;

  const trend = values.at(-1)! >= values[0]! ? 'trending up' : 'trending down';
  const end = points.at(-1)!;
  const hover = active === null ? null : points[active]!;
  const day = (i: number) =>
    new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);

  return (
    <div
      role="img"
      aria-label={`${label}: ${trend}`}
      className="relative w-full"
      style={{ height: HEIGHT }}
      onPointerMove={(e) => {
        const box = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - box.left) / box.width;
        setActive(
          Math.min(
            values.length - 1,
            Math.max(0, Math.round(ratio * (values.length - 1))),
          ),
        );
      }}
      onPointerLeave={() => setActive(null)}
    >
      {GRID.map((top) => (
        <div
          key={top}
          aria-hidden
          className="bg-border-subtle absolute inset-x-0 h-px opacity-60"
          style={{ top }}
        />
      ))}
      <svg
        aria-hidden
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        className="absolute inset-0 size-full overflow-visible"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity={0.28} />
            <stop offset="0.85" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradientId})`} />
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <Dot x={end[0]} y={end[1]} color={color} />

      {hover && active !== null && (
        <>
          <div
            aria-hidden
            className="bg-chart-crosshair pointer-events-none absolute inset-y-0 w-px"
            style={{ left: `${hover[0]}%` }}
          />
          <Dot x={hover[0]} y={hover[1]} color={color} ring />
          <div
            className="bg-bg-inverse text-caption text-text-inverse pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-sm px-2 py-1 whitespace-nowrap shadow-md"
            style={{
              left: `clamp(40px, ${hover[0]}%, calc(100% - 40px))`,
              top: Math.max(hover[1] - 8, 0),
            }}
          >
            {formatDay(day(active))} · {formatGain(values[active]!)}
          </div>
        </>
      )}
    </div>
  );
}

/** An 8px data point, positioned in HTML so it stays round as the SVG stretches. */
function Dot({
  x,
  y,
  color,
  ring,
}: {
  x: number;
  y: number;
  color: string;
  ring?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={
        ring
          ? 'border-bg-surface pointer-events-none absolute size-3 -translate-1/2 rounded-full border-2'
          : 'pointer-events-none absolute size-2 -translate-1/2 rounded-full'
      }
      style={{ left: `${x}%`, top: y, backgroundColor: color }}
    />
  );
}
